import { createFileRoute, Link } from "@tanstack/react-router";

import { LegalCallout, LegalDocument, LegalSection } from "@/features/legal/LegalDocument";
import { openCookiePreferences } from "@/lib/cookie-consent";

export const Route = createFileRoute("/legal/cookies")({
  head: () => ({
    meta: [
      { title: "Política de cookies | Socrattica" },
      {
        name: "description",
        content:
          "Cookies y almacenamiento local necesarios, Firebase Analytics opcional y gestión del consentimiento en Socrattica.",
      },
    ],
  }),
  component: CookiesPage,
});

function CookiesPage() {
  return (
    <LegalDocument
      eyebrow="Política de cookies"
      title="Analítica opcional, funciones necesarias."
      summary="Explicamos qué se guarda en tu navegador, para qué sirve, cuánto tiempo permanece y cómo aceptar o rechazar Firebase Analytics."
    >
      <LegalCallout title="Nuestra regla">
        <p>
          La cuenta, seguridad, carrito y preferencias usan almacenamiento necesario. Firebase
          Analytics no se carga hasta que aceptas la categoría “Analítica”. No usamos cookies de
          publicidad ni de perfilado comercial.
        </p>
        <button
          type="button"
          onClick={openCookiePreferences}
          className="mt-4 inline-flex cursor-pointer items-center rounded-lg bg-black px-4 py-2.5 text-xs font-semibold text-white"
        >
          Abrir preferencias de cookies
        </button>
      </LegalCallout>

      <LegalSection id="que-son" title="1. Qué son cookies y almacenamiento local">
        <p>
          Las cookies son pequeños datos que un sitio o proveedor guarda en el navegador.
          Tecnologías similares como localStorage y sessionStorage conservan preferencias, sesiones
          o información temporal. En esta política las agrupamos como “almacenamiento”.
        </p>
      </LegalSection>

      <LegalSection id="necesarias" title="2. Almacenamiento necesario">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-black">
                <th className="py-3 pr-4 font-semibold text-black">Nombre o grupo</th>
                <th className="py-3 pr-4 font-semibold text-black">Finalidad</th>
                <th className="py-3 font-semibold text-black">Duración aproximada</th>
              </tr>
            </thead>
            <tbody className="[&_td]:border-b [&_td]:border-black/10 [&_td]:py-3 [&_td]:pr-4 [&_td]:align-top">
              <tr>
                <td>
                  <code>socrattica.cookie-consent.v1</code>
                </td>
                <td>Recordar si permites Analytics.</td>
                <td>Hasta que cambies la decisión o borres datos.</td>
              </tr>
              <tr>
                <td>
                  <code>socrattica.cart.v2</code>
                </td>
                <td>Conservar los productos de tu carrito.</td>
                <td>Hasta que vacíes el carrito o borres datos.</td>
              </tr>
              <tr>
                <td>
                  <code>socrattica.display-currency</code> y caché de tasa
                </td>
                <td>Recordar moneda y evitar consultas repetidas.</td>
                <td>Preferencia persistente; tasa con caché temporal.</td>
              </tr>
              <tr>
                <td>
                  <code>socrattica:public-catalog:v1</code>
                </td>
                <td>Acelerar la carga del catálogo público.</td>
                <td>Durante la sesión del navegador.</td>
              </tr>
              <tr>
                <td>Claves de Firebase Authentication</td>
                <td>Mantener la sesión, verificar identidad y proteger descargas.</td>
                <td>Hasta cerrar sesión, vencer la sesión o borrar datos.</td>
              </tr>
              <tr>
                <td>Firebase App Check y reCAPTCHA Enterprise</td>
                <td>
                  Atestar silenciosamente que las solicitudes proceden del sitio legítimo y limitar
                  bots o abuso.
                </td>
                <td>Token temporal; normalmente se renueva durante la sesión.</td>
              </tr>
              <tr>
                <td>Preferencias del aviso del pódcast</td>
                <td>Evitar mostrar repetidamente el formulario y recordar una suscripción.</td>
                <td>Hasta que venza el intervalo o borres datos.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Estas tecnologías no se usan para publicidad. Bloquearlas desde el navegador puede impedir
          iniciar sesión, conservar el carrito o recordar tus preferencias.
        </p>
      </LegalSection>

      <LegalSection id="analytics" title="3. Firebase Analytics opcional">
        <p>
          Si aceptas, Google Analytics for Firebase puede crear <code>_ga</code> y{" "}
          <code>_ga_&lt;ID&gt;</code> para distinguir navegadores y medir visitas. Su duración
          habitual puede llegar a dos años y actualizarse con el uso. También recibe eventos como
          vista de página o producto, búsqueda, agregar al carrito, inicio de checkout, acceso,
          registro, descarga, suscripción y creación de soporte.
        </p>
        <p>
          No enviamos a Analytics contraseñas, contenido de tickets, correo, número de tarjeta ni el
          archivo descargado. Google puede procesar datos técnicos como identificador, dispositivo,
          navegador, IP y ubicación aproximada conforme a sus condiciones.
        </p>
      </LegalSection>

      <LegalSection id="terceros" title="4. Servicios externos">
        <p>
          Cuando eliges iniciar sesión con Google, abrir una plataforma de pódcast o continuar a una
          pasarela de pago, el proveedor externo puede usar sus propias cookies bajo su política.
          Esas tecnologías aparecen por una acción que solicitas y no están controladas por esta
          página.
        </p>
        <p>
          Las miniaturas de la sección de YouTube se cargan desde la infraestructura de Google. No
          insertamos el reproductor de YouTube dentro de Socrattica; al seleccionar un video se abre
          la plataforma externa. App Check y reCAPTCHA Enterprise funcionan como seguridad necesaria
          y no se usan para publicidad.
        </p>
      </LegalSection>

      <LegalSection id="control" title="5. Cómo controlar tu decisión">
        <ul>
          <li>Elige “Solo necesarias” o “Aceptar analítica” en el primer aviso.</li>
          <li>
            Usa “Preferencias de cookies” en el pie del sitio para cambiar la decisión en cualquier
            momento.
          </li>
          <li>
            Al rechazar Analytics, detenemos su recolección en este navegador e intentamos eliminar
            sus cookies propias.
          </li>
          <li>
            También puedes borrar cookies y almacenamiento desde los ajustes del navegador; esto
            puede cerrar la sesión y vaciar el carrito.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="datos" title="6. Relación con tus datos personales">
        <p>
          Cuando una cookie o identificador permite asociar información a una persona o dispositivo,
          lo tratamos conforme a la{" "}
          <Link to="/legal/privacidad">política de privacidad y tratamiento de datos</Link>. Puedes
          ejercer allí tus derechos y consultar los canales del responsable.
        </p>
      </LegalSection>

      <LegalSection id="cambios" title="7. Actualizaciones">
        <p>
          Actualizaremos esta tabla si incorporamos un proveedor, finalidad o tecnología relevante.
          Si una nueva categoría opcional requiere consentimiento, no se activará hasta que puedas
          elegirla.
        </p>
      </LegalSection>
    </LegalDocument>
  );
}
